import React, { useState } from 'react';
import {
  Box,
  Chip,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Tooltip,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { BaseSelectCellProps } from './LeadSelectCell';
import OptionMenuPaper from './OptionMenuPaper';
import { resolveOptionLabel } from '../../../utils/leads/leadsUtils';

export type LeadMultiSelectCellProps = BaseSelectCellProps;

export const LeadMultiSelectCell: React.FC<LeadMultiSelectCellProps> = ({
  value,
  options = [],
  onChange,
  onAddOption,
  onActionClick,
  actionText,
  placeholder = '—',
  menuWidth = 160,
  PaperProps,
  MenuListProps,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuMinWidth, setMenuMinWidth] = useState<number | null>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    const container =
      (event.currentTarget.closest('.cell-dropdown-container') as HTMLElement) || event.currentTarget;
    if (container) {
      setMenuMinWidth(container.offsetWidth);
    }
    setAnchorEl(container as HTMLElement);
  };

  const handleClose = (event?: any) => {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const stringOptions: string[] = options.map((opt) =>
    typeof opt === 'string' ? opt : (opt as any).label || (opt as any).value || String(opt)
  );

  let rawValues: any[] = [];
  if (Array.isArray(value)) {
    rawValues = value;
  } else if (typeof value === 'string' && value.trim() !== '') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          rawValues = parsed;
        } else {
          rawValues = [parsed];
        }
      } catch (e) {
        rawValues = trimmed.slice(1, -1).split(',').map((s) => s.trim().replace(/^['"]|['"]$/g, ''));
      }
    } else {
      rawValues = trimmed.split(',').map((s) => s.trim());
    }
  } else if (typeof value === 'number') {
    rawValues = [value];
  }

  const selectedIndices: number[] = rawValues
    .map((v) => {
      if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < stringOptions.length) {
        return v;
      }
      if (typeof v === 'string') {
        const matchIdx = stringOptions.findIndex((o) => o === v);
        if (matchIdx !== -1) return matchIdx;
        const numIdx = Number(v);
        if (!isNaN(numIdx) && Number.isInteger(numIdx) && numIdx >= 0 && numIdx < stringOptions.length) {
          return numIdx;
        }
      }
      return -1;
    })
    .filter((idx) => idx >= 0 && idx < stringOptions.length);

  const handleToggle = (index: number, event: React.MouseEvent) => {
    event.stopPropagation();
    let updated: number[];
    if (selectedIndices.includes(index)) {
      updated = selectedIndices.filter((i) => i !== index);
    } else {
      updated = [...selectedIndices, index];
    }
    onChange(updated);
  };

  const getItemLabel = (index: number) => stringOptions[index] || resolveOptionLabel(index, stringOptions);

  const maxVisible = 2;
  const visibleIndices = selectedIndices.slice(0, maxVisible);
  const extraCount = selectedIndices.length - maxVisible;
  const fullText = selectedIndices.map(getItemLabel).join(', ');
  const [justAddedIndex, setJustAddedIndex] = useState<number | null>(null);

  return (
    <Box
      className="cell-dropdown-container"
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        cursor: 'pointer',
      }}
    >
      {selectedIndices.length === 0 ? (
        <Chip
          label={placeholder}
          variant="outlined"
          color="default"
          deleteIcon={<KeyboardArrowDownIcon style={{ fontSize: '16px' }} />}
          onDelete={handleClick}
          sx={{
            width: '100%',
            height: '30px',
            borderRadius: '100px',
            display: 'inline-flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '0.75rem',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'primary.main',
            },
            '& .MuiChip-label': {
              paddingRight: '4px',
              paddingLeft: '10px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              flexGrow: 1,
              display: 'block',
              lineHeight: '30px',
              fontSize: '0.75rem',
            },
            '& .MuiChip-deleteIcon': {
              color: 'text.secondary',
              margin: '0 6px 0 2px',
              transition: 'color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                color: 'text.primary',
              },
            },
          }}
        />
      ) : (
        <Tooltip
          title={fullText}
          arrow
          placement="bottom-start"
          enterTouchDelay={0}
          leaveTouchDelay={3000}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'window',
                    padding: 12,
                  },
                },
              ],
            },
            tooltip: {
              sx: {
                maxWidth: 300,
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                fontSize: '0.75rem',
                lineHeight: 1.4,
                p: 1,
              },
            },
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              gap: 0.5,
              alignItems: 'center',
              overflow: 'hidden',
              py: 0.5,
              px: 1,
              width: '100%',
              height: '30px',
              borderRadius: '100px',
              border: '1px solid',
              borderColor: 'divider',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              boxSizing: 'border-box',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'primary.main',
              },
            }}
          >
            <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', overflow: 'hidden', minWidth: 0 }}>
              {visibleIndices.map((idx) => {
                const chipLabel = getItemLabel(idx);
                return (
                  <Chip
                    key={idx}
                    label={chipLabel}
                    size="small"
                    variant="outlined"
                    color="default"
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      maxWidth: extraCount > 0 ? 65 : selectedIndices.length > 1 ? 80 : 150,
                      flexShrink: 1,
                      minWidth: 0,
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        px: 0.75,
                      },
                    }}
                  />
                );
              })}
              {extraCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    ml: 0.25,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    userSelect: 'none',
                  }}
                >
                  +{extraCount} more
                </Typography>
              )}
            </Box>
            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          </Box>
        </Tooltip>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={
          PaperProps || {
            component: OptionMenuPaper,
            onAddOption: onAddOption ? async (newOptLabel: string) => {
              await onAddOption(newOptLabel);
              const newIdx = stringOptions.length;
              if (!selectedIndices.includes(newIdx)) {
                setJustAddedIndex(newIdx);
                onChange([...selectedIndices, newIdx]);
              }
              return newIdx;
            } : undefined,
            onActionClick: onActionClick ? () => {
              handleClose();
              onActionClick();
            } : undefined,
            actionText,
            style: {
              minWidth: menuMinWidth || menuWidth,
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
            },
          }
        }
        MenuListProps={MenuListProps}
      >
        {stringOptions.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              No options defined
            </Typography>
          </MenuItem>
        ) : (
          stringOptions.map((optLabel, index) => {
            const isChecked = selectedIndices.includes(index);
            return (
              <MenuItem
                key={`${optLabel}-${index}`}
                selected={isChecked}
                onClick={(e) => handleToggle(index, e)}
                ref={(node) => {
                  if (node && justAddedIndex === index) {
                    node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                    setJustAddedIndex(null);
                  }
                }}
                sx={{
                  py: 0.5,
                  px: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.50',
                    fontWeight: 600,
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.100',
                    },
                  },
                }}
              >
                <Checkbox checked={isChecked} size="small" sx={{ p: 0.5, mr: 1 }} />
                <ListItemText
                  primary={optLabel}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: isChecked ? 600 : 400,
                  }}
                />
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
};

export default LeadMultiSelectCell;
